import React, { useState, useRef, useEffect } from 'react';

const VERIFY_PIN_URL = '/.netlify/functions/staff-verify-pin';
const LOOKUP_URL     = '/.netlify/functions/staff-lookup';

// ─── Styles ────────────────────────────────────────────────────────────────
const s = {
  page: { minHeight:'100vh', background:'#0F172A', color:'#fff', fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif", display:'flex', flexDirection:'column', alignItems:'center' },
  container: { width:'100%', maxWidth:480, padding:'0 20px 40px', display:'flex', flexDirection:'column', minHeight:'100vh' },
  header: { textAlign:'center', padding:'28px 0 20px', borderBottom:'1px solid rgba(255,255,255,0.1)', marginBottom:28 },
  badge: { display:'inline-block', background:'linear-gradient(135deg,#1E40AF,#3B82F6)', fontSize:10, fontWeight:700, padding:'4px 14px', borderRadius:20, letterSpacing:1, textTransform:'uppercase', color:'#fff', marginBottom:8 },
  h1: { fontSize:20, fontWeight:700, color:'#2B9DD4', letterSpacing:1, margin:0 },
  sub: { fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:4 },

  // PIN screen
  pinWrap: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:20 },
  lock: { width:68, height:68, borderRadius:'50%', background:'rgba(59,184,232,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30 },
  pinH2: { fontSize:18, fontWeight:600, margin:0 },
  pinInput: { width:200, textAlign:'center', fontSize:32, letterSpacing:12, background:'rgba(255,255,255,0.08)', border:'2px solid rgba(255,255,255,0.15)', borderRadius:14, padding:14, color:'#fff', outline:'none' },
  pinMsg: { fontSize:13, minHeight:20, textAlign:'center' },

  // Search screen
  searchBox: { position:'relative', marginBottom:16 },
  searchIcon: { position:'absolute', left:16, top:'50%', transform:'translateY(-50%)', fontSize:18, color:'rgba(255,255,255,0.3)' },
  searchInput: { width:'100%', padding:'15px 16px 15px 46px', background:'rgba(255,255,255,0.08)', border:'2px solid rgba(255,255,255,0.12)', borderRadius:14, fontSize:16, color:'#fff', outline:'none', boxSizing:'border-box' },
  searchBtn: { width:'100%', padding:14, background:'linear-gradient(135deg,#2B9DD4,#1E8EC4)', border:'none', borderRadius:12, fontSize:15, fontWeight:700, color:'#fff', cursor:'pointer', marginTop:4 },
  searchBtnDisabled: { opacity:0.5, cursor:'not-allowed' },

  // Result card
  card: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, padding:20, marginTop:16 },
  cardVip: { borderColor:'rgba(59,184,232,0.4)', background:'linear-gradient(135deg,rgba(59,184,232,0.1),rgba(30,142,196,0.05))' },
  cardExpired: { borderColor:'rgba(248,113,113,0.4)', background:'rgba(248,113,113,0.06)' },
  statusRow: { display:'flex', alignItems:'center', gap:10, marginBottom:14 },
  dot: { width:13, height:13, borderRadius:'50%' },
  dotActive: { background:'#22C55E', boxShadow:'0 0 8px rgba(34,197,94,0.5)' },
  dotExpired: { background:'#F87171', boxShadow:'0 0 8px rgba(248,113,113,0.5)' },
  dotNone: { background:'#6B7280' },
  statusTxt: { fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:1 },
  memberName: { fontSize:22, fontWeight:700, marginBottom:4 },
  memberPhone: { fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:16 },
  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
  infoItem: { background:'rgba(255,255,255,0.05)', borderRadius:10, padding:12 },
  infoLabel: { fontSize:10, textTransform:'uppercase', letterSpacing:0.8, color:'rgba(255,255,255,0.4)', marginBottom:4 },
  infoVal: { fontSize:14, fontWeight:600 },
  gold: { color:'#F5C842' },
  blue: { color:'#2B9DD4' },
  green: { color:'#22C55E' },
  red: { color:'#F87171' },
  sourceBadge: { fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:6, marginLeft:8 },

  // Family
  familySec: { marginTop:14 },
  familyTitle: { fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'rgba(255,255,255,0.4)', marginBottom:8 },
  familyRow: { display:'flex', alignItems:'center', gap:10, padding:'9px 12px', background:'rgba(255,255,255,0.04)', borderRadius:10, marginBottom:6 },
  familySlot: { width:28, height:28, borderRadius:8, background:'rgba(59,184,232,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#2B9DD4' },
  familyName: { fontSize:13, fontWeight:600, flex:1 },
  familyRel: { fontSize:11, color:'rgba(255,255,255,0.4)' },

  // Misc
  notFound: { textAlign:'center', padding:'36px 20px', color:'rgba(255,255,255,0.45)', fontSize:14 },
  loading: { textAlign:'center', padding:'36px', color:'rgba(255,255,255,0.4)', fontSize:14 },
  lockBtn: { display:'block', textAlign:'center', padding:'16px', color:'rgba(255,255,255,0.25)', fontSize:12, cursor:'pointer', marginTop:'auto', background:'none', border:'none', width:'100%' },
  bcNote: { marginTop:12, padding:'10px 14px', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:10, fontSize:12, color:'#F59E0B' },
};

export default function StaffVerifyPage() {
  const [unlocked, setUnlocked]     = useState(false);
  const [sessionToken, setToken]    = useState('');
  const [pin, setPin]               = useState('');
  const [pinMsg, setPinMsg]         = useState({ text:'', color:'#F87171' });
  const [pinBusy, setPinBusy]       = useState(false);

  const [phone, setPhone]           = useState('');
  const [searching, setSearching]   = useState(false);
  const [result, setResult]         = useState(null); // null | 'not-found' | member object
  const phoneRef                    = useRef(null);

  // Auto-verify when 4 digits entered
  useEffect(() => {
    if (pin.length === 4 && !pinBusy) verifyPin(pin);
  }, [pin]);

  async function verifyPin(value) {
    setPinBusy(true);
    setPinMsg({ text: 'Verifying...', color: '#2B9DD4' });
    try {
      const r = await fetch(VERIFY_PIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: value }),
      });
      const d = await r.json();
      if (d.valid) {
        setToken(d.token);
        setUnlocked(true);
        setTimeout(() => phoneRef.current?.focus(), 100);
      } else {
        setPinMsg({ text: 'Invalid PIN', color: '#F87171' });
        setPin('');
      }
    } catch (e) {
      setPinMsg({ text: 'Connection error: ' + e.message, color: '#F87171' });
      setPin('');
    }
    setPinBusy(false);
  }

  async function searchMember() {
    if (!phone.trim() || searching) return;
    setSearching(true);
    setResult('loading');
    try {
      const r = await fetch(`${LOOKUP_URL}?phone=${encodeURIComponent(phone.trim())}`, {
        headers: { 'X-Staff-Token': sessionToken },
      });
      const d = await r.json();
      setResult(d.found ? d : 'not-found');
    } catch {
      setResult('error');
    }
    setSearching(false);
  }

  function lock() {
    setUnlocked(false);
    setToken('');
    setPin('');
    setPhone('');
    setResult(null);
    setPinMsg({ text: '', color: '#F87171' });
  }

  // ── PIN Screen ─────────────────────────────────────────────────────────
  if (!unlocked) {
    return (
      <div style={s.page}>
        <div style={s.container}>
          <div style={s.header}>
            <div style={s.badge}>Staff Only</div>
            <h1 style={s.h1}>The Toy Store VIP</h1>
            <p style={s.sub}>Member Verification System</p>
          </div>
          <div style={s.pinWrap}>
            <div style={s.lock}>🔒</div>
            <h2 style={s.pinH2}>Enter Staff PIN</h2>
            <input
              style={s.pinInput}
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder="••••"
              value={pin}
              autoFocus
              onChange={e => { setPinMsg({ text:'', color:'#F87171' }); setPin(e.target.value.replace(/\D/g,'')); }}
              onKeyDown={e => e.key === 'Enter' && pin.length >= 4 && verifyPin(pin)}
            />
            <p style={{ ...s.pinMsg, color: pinMsg.color }}>{pinMsg.text || ' '}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Search Screen ──────────────────────────────────────────────────────
  const m = result && result !== 'not-found' && result !== 'loading' && result !== 'error' ? result.member : null;
  const isActive  = m?.membershipStatus === 'active';
  const isExpired = m?.membershipStatus === 'expired';

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <div style={s.badge}>Staff Only</div>
          <h1 style={s.h1}>The Toy Store VIP</h1>
          <p style={s.sub}>Member Verification System</p>
        </div>

        {/* Search box */}
        <div style={s.searchBox}>
          <span style={s.searchIcon}>🔍</span>
          <input
            ref={phoneRef}
            style={s.searchInput}
            type="tel"
            placeholder="Enter phone number..."
            value={phone}
            onChange={e => setPhone(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchMember()}
          />
        </div>
        <button
          style={{ ...s.searchBtn, ...(searching ? s.searchBtnDisabled : {}) }}
          onClick={searchMember}
          disabled={searching}
        >
          {searching ? 'Searching...' : 'Verify Member'}
        </button>

        {/* Result */}
        {result === 'loading' && <div style={s.loading}>Looking up member...</div>}

        {result === 'not-found' && (
          <div style={s.notFound}>❌&nbsp; No member found with this phone number.</div>
        )}

        {(result === 'error') && (
          <div style={s.notFound}>⚠️&nbsp; Connection error. Try again.</div>
        )}

        {m && (
          <div style={{ ...s.card, ...(isActive ? s.cardVip : isExpired ? s.cardExpired : {}) }}>
            {/* Status row */}
            <div style={s.statusRow}>
              <div style={{ ...s.dot, ...(isActive ? s.dotActive : isExpired ? s.dotExpired : s.dotNone) }} />
              <span style={{ ...s.statusTxt, ...(isActive ? s.green : isExpired ? s.red : { color:'#6B7280' }) }}>
                {isActive ? 'VIP Active' : isExpired ? 'VIP Expired' : 'No Membership'}
              </span>
              <span style={{
                ...s.sourceBadge,
                background: result.source === 'bc' ? 'rgba(245,158,11,0.2)' : 'rgba(59,184,232,0.2)',
                color: result.source === 'bc' ? '#F59E0B' : '#2B9DD4',
              }}>
                {result.source === 'bc' ? 'BC' : 'APP'}
              </span>
            </div>

            <div style={s.memberName}>{m.name}</div>
            <div style={s.memberPhone}>{m.phone}{m.email ? ` · ${m.email}` : ''}</div>

            <div style={s.grid}>
              {m.memberCode && (
                <div style={s.infoItem}>
                  <div style={s.infoLabel}>Member Code</div>
                  <div style={{ ...s.infoVal, ...s.blue }}>{m.memberCode}</div>
                </div>
              )}
              {m.expiryDate && (
                <div style={s.infoItem}>
                  <div style={s.infoLabel}>Expires</div>
                  <div style={s.infoVal}>{new Date(m.expiryDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
                </div>
              )}
              {m.daysLeft != null && (
                <div style={s.infoItem}>
                  <div style={s.infoLabel}>Days Left</div>
                  <div style={{ ...s.infoVal, ...s.gold }}>{m.daysLeft} days</div>
                </div>
              )}
              <div style={s.infoItem}>
                <div style={s.infoLabel}>Points</div>
                <div style={{ ...s.infoVal, ...s.gold }}>{m.points || 0} pts</div>
              </div>
              <div style={s.infoItem}>
                <div style={s.infoLabel}>Joined</div>
                <div style={s.infoVal}>{new Date(m.joinedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
              </div>
              {m.renewals !== undefined && (
                <div style={s.infoItem}>
                  <div style={s.infoLabel}>Renewals</div>
                  <div style={s.infoVal}>{m.renewals}</div>
                </div>
              )}
            </div>

            {/* Family members */}
            {m.familyMembers?.length > 0 && (
              <div style={s.familySec}>
                <div style={s.familyTitle}>Family Cards ({m.familyMembers.length}/4)</div>
                {m.familyMembers.map((f, i) => (
                  <div key={i} style={s.familyRow}>
                    <div style={s.familySlot}>F{f.slot}</div>
                    <div style={s.familyName}>{f.name}</div>
                    <div style={s.familyRel}>{f.relationship}</div>
                  </div>
                ))}
              </div>
            )}

            {m.bcNote && <div style={s.bcNote}>{m.bcNote}</div>}
          </div>
        )}

        <button style={s.lockBtn} onClick={lock}>🔒 Lock Session</button>
      </div>
    </div>
  );
}
